import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  Activity,
  CalendarDays,
  ChevronDown,
  Clock3,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
  Wifi,
  WifiOff,
  Zap,
} from "lucide-react";

import "./GreeksAnalyser.css";
import { environment } from "environment/environment";
import usePageTitle from "component/common/PageTitle";
import useAngelOneGreeksWebsocket from "service/websocket/AngelOneGreeksWebsocket";
import useAngelOneGammaBlast from "service/api/AngelOneGammaBlastApi";
import useAngelOneGammaBlastPremiumMapHistory from "service/api/AngelOneGammaBlastPremiumMapHistoryApi";
import useAngelOneRuntimeMarketData from "service/api/AngelOneRuntimeMarketDataApi";

ChartJS.register(CategoryScale, Filler, Legend, LinearScale, LineElement, PointElement, Title, ChartTooltip);

const METRICS = [
  { key: "vega", label: "VEGA", eyebrow: "VOLATILITY FLOW", precision: 2, icon: Zap },
  { key: "iv", label: "IMPLIED VOLATILITY", eyebrow: "PREMIUM PRESSURE", precision: 2, icon: Activity },
  { key: "gamma", label: "GAMMA", eyebrow: "CONVEXITY FLOW", precision: 4, icon: TrendingUp },
];

const ANALYSER_TABS = [
  { key: "GAMMA_BLAST", label: "GAMMA BLAST" },
  { key: "GREEKS_ANALYSER", label: "GREEKS ANALYSER" },
  { key: "GREEKS_GRAPH", label: "GREEKS GRAPH" },
];

const MAX_INTRADAY_POINTS = 390;
const MARKET_TIME_ZONE = "Asia/Kolkata";

const marketDate = (value = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: MARKET_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const values = Object.fromEntries(parts.map(({ type, value: partValue }) => [type, partValue]));
  return `${values.year}-${values.month}-${values.day}`;
};

const dateFromInput = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const result = new Date(0);
  result.setHours(0, 0, 0, 0);
  result.setFullYear(year, month - 1, day);
  return result;
};

const inputDateFromDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  const pad = (part) => String(part).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

const asNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const parsed = Number(String(value).replace(/,/g, ""));
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const metricSnapshot = (value) => ({
  call: asNumber(value?.call),
  put: asNumber(value?.put),
  difference: asNumber(value?.difference),
});

const emptyMetric = () => ({ call: null, put: null, difference: null });

const metricValuesForPoint = (point, metric) => {
  if (metric.key === "vega" || metric.key === "iv" || metric.key === "gamma") {
    const storedChange = point?.changeMetrics?.[metric.key];
    if (storedChange && (storedChange.call !== null || storedChange.put !== null)) {
      return storedChange;
    }
  }
  return point?.metrics?.[metric.key] || emptyMetric();
};

const snapshotFromAngelOne = (source) => {
  if (!source) return null;

  const metrics = {
    vega: metricSnapshot(source.currentVega),
    iv: metricSnapshot(source.currentIv),
    gamma: metricSnapshot(source.currentGamma),
  };
  const openingMetrics = {
    vega: metricSnapshot(source.dayOpenVega),
    iv: metricSnapshot(source.dayOpenIv),
    gamma: metricSnapshot(source.dayOpenGamma),
  };
  const changeMetrics = {
    vega: metricSnapshot(source.changeVega),
    iv: metricSnapshot(source.changeIv),
    gamma: metricSnapshot(source.changeGamma),
  };
  const hasMetric = METRICS.some((metric) => (
    metrics[metric.key].call !== null || metrics[metric.key].put !== null
  ));
  if (!hasMetric) return null;

  return {
    id: `${source.accountName || "angelone"}-${source.createdDate || "live"}-${source.createdTime || "now"}`,
    time: source.createdTime || "--",
    date: source.createdDate || "",
    expiry: source.expiryDate || "--",
    spot: asNumber(source.indexSpotPrice),
    metrics,
    openingMetrics,
    changeMetrics,
  };
};

const formatValue = (value, precision = 2) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "--";
  return Number(value).toFixed(precision);
};

const signedValue = (value, precision = 2) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "--";
  const number = Number(value);
  return `${number > 0 ? "+" : ""}${number.toFixed(precision)}`;
};

const diff = (metric) => {
  if (metric?.call === null || metric?.put === null) return null;
  return metric.call - metric.put;
};

const graphDiff = (metric) => metric?.difference ?? diff(metric);

const change = (current, opening) => ({
  call: current?.call !== null && opening?.call !== null ? current.call - opening.call : null,
  put: current?.put !== null && opening?.put !== null ? current.put - opening.put : null,
});

const trendFor = (value) => (value > 0 ? "BEARISH" : value < 0 ? "BULLISH" : "NEUTRAL");

const trendToneFor = (value) => (value > 0 ? "negative" : value < 0 ? "positive" : "neutral");

const toneFor = (value) => (value > 0 ? "positive" : value < 0 ? "negative" : "neutral");

const formatHeaderDate = (value) => {
  if (!value) return "--";
  const normalized = String(value).slice(0, 10);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : String(value);
};

const formatHeaderTime = (value) => {
  if (!value) return "--";
  if (typeof value === "number") {
    return new Date(value).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "Asia/Kolkata",
    });
  }
  const match = String(value).match(/(\d{2}:\d{2})(?::(\d{2}))?/);
  return match ? `${match[1]}${match[2] ? `:${match[2]}` : ""}` : String(value);
};

function MetricSummaryCard({ metric, summary }) {
  const Icon = metric.icon;
  const rows = [
    { label: "DAY OPEN", values: summary.opening, tone: "" },
    { label: "CURRENT", values: summary.current, tone: "" },
    { label: "DIFFERENCE", values: summary.change, tone: "difference" },
  ];

  return (
    <article className={`greeks-metric-card metric-${metric.key}`}>
      <div className="metric-card-heading">
        <div className="metric-heading-icon"><Icon size={18} strokeWidth={2.3} /></div>
        <div>
          <h2>{metric.label} <span className="metric-heading-flow">{metric.eyebrow}</span></h2>
        </div>
        <span className={`metric-trend ${trendToneFor(summary.netChange)}`}>{trendFor(summary.netChange)}</span>
      </div>

      <div className="metric-table" role="table" aria-label={`${metric.label} summary`}>
        <div className="metric-table-row metric-table-head" role="row">
          <span>SNAPSHOT</span><span>CALL {graphMetricLabel(metric)}</span><span>PUT {graphMetricLabel(metric)}</span><span>DIFF</span>
        </div>
        {rows.map((row) => (
          <div className={`metric-table-row ${row.tone}`} role="row" key={row.label}>
            <strong>{row.label}</strong>
            <span className={row.tone === "difference" ? toneFor(row.values.call) : ""}>{row.tone === "difference" ? signedValue(row.values.call, metric.precision) : formatValue(row.values.call, metric.precision)}</span>
            <span className={row.tone === "difference" ? toneFor(row.values.put) : ""}>{row.tone === "difference" ? signedValue(row.values.put, metric.precision) : formatValue(row.values.put, metric.precision)}</span>
            <span className={row.tone === "difference" ? toneFor(row.values.diff) : ""}>{row.tone === "difference" ? signedValue(row.values.diff, metric.precision) : signedValue(row.values.diff, metric.precision)}</span>
          </div>
        ))}
      </div>
    </article>
  );
}

function HistoryCard({ metric, history }) {
  const Icon = metric.icon;

  return (
    <section className={`greeks-history-card metric-${metric.key}`}>
      <div className="history-card-heading">
        <div className="history-card-title">
          <div className="metric-heading-icon"><Icon size={18} strokeWidth={2.3} /></div>
          <h2>{metric.label} <span className="history-heading-flow">INTRADAY TRACKER</span></h2>
        </div>
        <span className="history-count">{history.length} points</span>
      </div>
      <div className="history-table-wrap">
        <table className="history-table">
          <thead>
            <tr><th>TIME</th><th>CALL {graphMetricLabel(metric)}</th><th>PUT {graphMetricLabel(metric)}</th><th>DIFF</th><th>TREND</th></tr>
          </thead>
          <tbody>
            {[...history].reverse().map((point) => {
              const values = metricValuesForPoint(point, metric);
              const difference = diff(values);
              return (
                <tr key={`${metric.key}-${point.id || point.time}`}>
                  <td className="history-time">{point.time}</td>
                  <td>{formatValue(values?.call, metric.precision)}</td>
                  <td>{formatValue(values?.put, metric.precision)}</td>
                  <td className={toneFor(difference)}>{signedValue(difference, metric.precision)}</td>
                  <td><span className={`table-trend ${trendToneFor(difference)}`}>{trendFor(difference)}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const eventSignalTone = (signal) => {
  if (signal === "BULLISH") return "bullish";
  if (signal === "BEARISH") return "bearish";
  if (signal === "VOLATILITY") return "volatility";
  return "neutral";
};

const eventTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  });
};

const GAMMA_BLAST_TRIGGER_SCORE = 75;
const GAMMA_BLAST_REQUIRED_READINGS = 3;
const GAMMA_BLAST_VISIBLE_STRIKE_RADIUS = 5;

const gammaBlastLiveState = (latest, latestEvent) => {
  if (latestEvent) {
    return {
      label: latestEvent.signal || "EVENT",
      description: `${eventTime(latestEvent.signalTimeUtc)} IST · ${latestEvent.result || "OPEN"}`,
    };
  }

  if (!latest) {
    return {
      label: "WAITING FOR DATA",
      description: "The first detector reading has not arrived yet.",
    };
  }

  if (latest.status === "READY") {
    return {
      label: "MONITORING",
      description: "Live data received; no qualifying Gamma Blast signal yet.",
    };
  }

  if (latest.status === "WARMING_UP") {
    return {
      label: "WARMING UP",
      description: latest.reason || "Building the reference window required for scoring.",
    };
  }

  return {
    label: "PARTIAL DATA",
    description: latest.reason || "Waiting for a complete Angel One Greeks reading.",
  };
};

function GammaBlastSpotRow({ runningSpot, priceRange, hasPriceRange }) {
  if (runningSpot === null) return null;

  return (
    <tr className="gamma-blast-projection-spot-row" aria-label={`Running spot ${formatValue(runningSpot, 2)}`}>
      <td colSpan="15">
        {hasPriceRange ? (
          <div className="gamma-blast-spot-range" aria-label="Estimated range at running spot">
            <div className="gamma-blast-spot-range-value top">
              <span>ESTIMATED TOP</span>
              <strong>{formatValue(priceRange.estimatedTop, 2)}</strong>
            </div>
            <div className="gamma-blast-spot-range-value move">
              <span>SPOT {formatValue(runningSpot, 2)} · EXPECTED MOVE</span>
              <strong>±{formatValue(priceRange.expectedMovePoints, 2)}</strong>
            </div>
            <div className="gamma-blast-spot-range-value bottom">
              <span>ESTIMATED BOTTOM</span>
              <strong>{formatValue(priceRange.estimatedBottom, 2)}</strong>
            </div>
          </div>
        ) : (
          <span className="gamma-blast-spot-label">SPOT {formatValue(runningSpot, 2)}</span>
        )}
      </td>
    </tr>
  );
}

const premiumScenarioRowsFrom = (premiumProjections) => Array.from(
  (Array.isArray(premiumProjections) ? premiumProjections : []).reduce((rows, projection) => {
    const strike = asNumber(projection?.strikePrice);
    const optionType = String(projection?.optionType || "").toUpperCase();
    if (strike === null || (optionType !== "CE" && optionType !== "PE")) return rows;

    const key = String(strike);
    const row = rows.get(key) || { strike, call: null, put: null };
    row[optionType === "CE" ? "call" : "put"] = projection;
    rows.set(key, row);
    return rows;
  }, new Map()).values(),
).sort((left, right) => left.strike - right.strike);

const visiblePremiumScenarioRowsFrom = (rows, runningSpot) => {
  if (runningSpot === null || rows.length <= (GAMMA_BLAST_VISIBLE_STRIKE_RADIUS * 2) + 1) return rows;

  const nearestSpotStrikeIndex = rows.reduce((closestIndex, row, rowIndex, allRows) => (
    Math.abs(row.strike - runningSpot) < Math.abs(allRows[closestIndex].strike - runningSpot)
      ? rowIndex
      : closestIndex
  ), 0);
  const visibleRowCount = (GAMMA_BLAST_VISIBLE_STRIKE_RADIUS * 2) + 1;
  const maxStartIndex = rows.length - visibleRowCount;
  const startIndex = Math.max(
    0,
    Math.min(nearestSpotStrikeIndex - GAMMA_BLAST_VISIBLE_STRIKE_RADIUS, maxStartIndex),
  );
  return rows.slice(startIndex, startIndex + visibleRowCount);
};

const spotRowIndexFrom = (rows, runningSpot) => {
  if (runningSpot === null) return -1;
  const nextStrikeIndex = rows.findIndex(({ strike }) => strike > runningSpot);
  return nextStrikeIndex === -1 ? rows.length : nextStrikeIndex;
};

function GammaBlastPremiumScenarioTable({ rows, runningSpot, priceRange, ariaLabel }) {
  const hasPriceRange = priceRange && Number.isFinite(Number(priceRange.expectedMovePoints));
  const spotRowIndex = spotRowIndexFrom(rows, runningSpot);

  return (
    <div className="gamma-blast-projection-wrap">
      <table className="gamma-blast-projection-table" aria-label={ariaLabel}>
        <thead>
          <tr>
            <th className="gamma-blast-projection-call-header">THETA</th>
            <th className="gamma-blast-projection-call-header">VEGA</th>
            <th className="gamma-blast-projection-call-header">GAMMA</th>
            <th className="gamma-blast-projection-call-header">DELTA</th>
            <th className="gamma-blast-projection-call-header">TOP</th>
            <th className="gamma-blast-projection-call-header">BOTTOM</th>
            <th className="gamma-blast-projection-call-header">CURRENT</th>
            <th className="gamma-blast-projection-strike-heading">STRIKE</th>
            <th className="gamma-blast-projection-put-header">CURRENT</th>
            <th className="gamma-blast-projection-put-header">BOTTOM</th>
            <th className="gamma-blast-projection-put-header">TOP</th>
            <th className="gamma-blast-projection-put-header">DELTA</th>
            <th className="gamma-blast-projection-put-header">GAMMA</th>
            <th className="gamma-blast-projection-put-header">VEGA</th>
            <th className="gamma-blast-projection-put-header">THETA</th>
          </tr>
        </thead>
        <tbody>
          {spotRowIndex === 0 && (
            <GammaBlastSpotRow
              runningSpot={runningSpot}
              priceRange={priceRange}
              hasPriceRange={hasPriceRange}
            />
          )}
          {rows.map(({ strike, call, put }, rowIndex) => (
            <React.Fragment key={strike}>
              {spotRowIndex === rowIndex && spotRowIndex !== 0 && (
                <GammaBlastSpotRow
                  runningSpot={runningSpot}
                  priceRange={priceRange}
                  hasPriceRange={hasPriceRange}
                />
              )}
              <tr>
                <td className="gamma-blast-projection-call-greek">{formatValue(call?.theta, 4)}</td>
                <td className="gamma-blast-projection-call-greek">{formatValue(call?.vega, 4)}</td>
                <td className="gamma-blast-projection-call-greek">{formatValue(call?.gamma, 4)}</td>
                <td className="gamma-blast-projection-call-greek">{formatValue(call?.delta, 4)}</td>
                <td className="gamma-blast-projection-call-side gamma-blast-projection-top">{formatValue(call?.estimatedTopPremium, 2)}</td>
                <td className="gamma-blast-projection-call-side gamma-blast-projection-bottom">{formatValue(call?.estimatedBottomPremium, 2)}</td>
                <td className="gamma-blast-projection-call-side">{formatValue(call?.currentPremium, 2)}</td>
                <td className="gamma-blast-projection-strike">{formatValue(strike, 2)}</td>
                <td className="gamma-blast-projection-put-side">{formatValue(put?.currentPremium, 2)}</td>
                <td className="gamma-blast-projection-put-side gamma-blast-projection-bottom">{formatValue(put?.estimatedBottomPremium, 2)}</td>
                <td className="gamma-blast-projection-put-side gamma-blast-projection-top">{formatValue(put?.estimatedTopPremium, 2)}</td>
                <td className="gamma-blast-projection-put-greek">{formatValue(put?.delta, 4)}</td>
                <td className="gamma-blast-projection-put-greek">{formatValue(put?.gamma, 4)}</td>
                <td className="gamma-blast-projection-put-greek">{formatValue(put?.vega, 4)}</td>
                <td className="gamma-blast-projection-put-greek">{formatValue(put?.theta, 4)}</td>
              </tr>
            </React.Fragment>
          ))}
          {spotRowIndex === rows.length && (
            <GammaBlastSpotRow
              runningSpot={runningSpot}
              priceRange={priceRange}
              hasPriceRange={hasPriceRange}
            />
          )}
        </tbody>
      </table>
    </div>
  );
}

function GammaBlastPremiumMapHistory({
  history,
  loading,
  error,
  onReload,
  sessionDate,
  onSessionDateChange,
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const detailRef = useRef(null);
  const [detailHeight, setDetailHeight] = useState(null);
  const safeSelectedIndex = history.length ? Math.min(selectedIndex, history.length - 1) : 0;
  const selected = history[safeSelectedIndex];
  const projections = selected?.premiumProjections || [];
  const allRows = premiumScenarioRowsFrom(projections);
  const runningSpot = asNumber(selected?.spotPrice);
  const rows = visiblePremiumScenarioRowsFrom(allRows, runningSpot);
  const bucketTime = selected?.bucketCapturedAtUtc || selected?.sourceCapturedAtUtc;
  const sourceTime = selected?.sourceCapturedAtUtc || bucketTime;

  useEffect(() => {
    setSelectedIndex(0);
  }, [sessionDate]);

  useLayoutEffect(() => {
    const detailElement = detailRef.current;
    if (!detailElement) return undefined;

    const updateDetailHeight = () => {
      const nextHeight = Math.ceil(detailElement.getBoundingClientRect().height);
      setDetailHeight((previousHeight) => (previousHeight === nextHeight ? previousHeight : nextHeight));
    };

    updateDetailHeight();
    const ResizeObserverConstructor = typeof window !== "undefined" ? window.ResizeObserver : undefined;
    if (!ResizeObserverConstructor) return undefined;

    const observer = new ResizeObserverConstructor(updateDetailHeight);
    observer.observe(detailElement);
    return () => observer.disconnect();
  }, [bucketTime, rows.length, safeSelectedIndex]);

  return (
    <section className="gamma-blast-premium-history" aria-label="Five-minute premium map history">
      <div className="gamma-blast-premium-history-heading">
        <div>
          <span className="gamma-blast-label">ARCHIVED HISTORY</span>
          <h2>Five-minute premium scenario maps</h2>
          <p>Historical call and put premium estimates for {formatHeaderDate(sessionDate)}.</p>
        </div>
        <div className="gamma-blast-premium-history-actions">
          <span className="gamma-blast-projection-count">{history.length} snapshots</span>
          <label className="gamma-blast-premium-history-date">
            <span>HISTORY DATE</span>
            <DatePicker
              selected={dateFromInput(sessionDate)}
              onChange={(value) => {
                const nextDate = inputDateFromDate(value);
                if (nextDate) onSessionDateChange(nextDate);
              }}
              maxDate={dateFromInput(marketDate())}
              dateFormat="dd/MM/yyyy"
              className="gamma-blast-premium-history-date-input"
              calendarClassName="gamma-blast-premium-history-calendar"
              popperClassName="gamma-blast-premium-history-popper"
              popperPlacement="bottom-end"
              showPopperArrow={false}
              todayButton="Today"
              aria-label="Premium history date"
            />
          </label>
          <button type="button" className="gamma-blast-reload" onClick={onReload} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="gamma-blast-feedback error" role="alert">
          Five-minute premium-map history is temporarily unavailable.
        </div>
      ) : loading && !history.length ? (
        <div className="gamma-blast-feedback">Loading five-minute premium-map history…</div>
      ) : !history.length ? (
        <div className="gamma-blast-feedback">
          No five-minute premium maps were found for {formatHeaderDate(sessionDate)}.
        </div>
      ) : (
        <div className="gamma-blast-premium-history-layout">
          <div
            className="gamma-blast-premium-history-list"
            role="tablist"
            aria-label="Archived premium map times"
            style={detailHeight ? { height: `${detailHeight}px` } : undefined}
          >
            {history.map((snapshot, index) => {
              const time = snapshot?.bucketCapturedAtUtc || snapshot?.sourceCapturedAtUtc;
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={index === safeSelectedIndex}
                  className={`gamma-blast-premium-history-item ${index === safeSelectedIndex ? "active" : ""}`}
                  onClick={() => setSelectedIndex(index)}
                  key={snapshot?.id || `${time}-${index}`}
                >
                  <strong>{eventTime(time)} IST</strong>
                  <span>Spot {formatValue(snapshot?.spotPrice, 2)}</span>
                  <small>{Array.isArray(snapshot?.premiumProjections) ? `${premiumScenarioRowsFrom(snapshot.premiumProjections).length} strikes` : "No map"}</small>
                </button>
              );
            })}
          </div>

          <div ref={detailRef} className="gamma-blast-premium-history-detail" role="tabpanel">
            <div className="gamma-blast-premium-history-detail-heading">
              <div>
                <span className="gamma-blast-label">SELECTED MAP</span>
                <strong>{eventTime(bucketTime)} IST</strong>
              </div>
              <span>Source reading {eventTime(sourceTime)} IST · {rows.length} strikes shown</span>
            </div>
            <GammaBlastPremiumScenarioTable
              rows={rows}
              runningSpot={runningSpot}
              priceRange={selected?.priceRange}
              ariaLabel={`Archived premium scenario map at ${eventTime(bucketTime)} IST`}
            />
            <div className="gamma-blast-premium-history-note">
              <span>Stored in five-minute bucket {eventTime(bucketTime)} IST</span>
              <small>Top and bottom are estimates from the saved Greek scenario, not guaranteed market highs or lows.</small>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function GammaBlastMonitor({
  latest,
  events,
  stats,
  loading,
  error,
  onReload,
  premiumMapHistory,
  premiumMapHistoryLoading,
  premiumMapHistoryError,
  onReloadPremiumMapHistory,
  premiumMapHistoryDate,
  onPremiumMapHistoryDateChange,
}) {
  const [premiumView, setPremiumView] = useState("CURRENT");
  const latestEvent = events[0];
  const liveState = gammaBlastLiveState(latest, latestEvent);
  const signal = liveState.label;
  const signalTone = eventSignalTone(latestEvent?.signal);
  const latestCallScore = latest?.callScore ?? latestEvent?.callScore;
  const latestPutScore = latest?.putScore ?? latestEvent?.putScore;
  const hasLiveScores = Number.isFinite(Number(latestCallScore)) || Number.isFinite(Number(latestPutScore));
  const strongestScore = Math.max(Number(latestCallScore) || 0, Number(latestPutScore) || 0);
  const scoreProgress = Math.min(100, (strongestScore / GAMMA_BLAST_TRIGGER_SCORE) * 100);
  const priceRange = latest?.priceRange || latestEvent?.priceRange;
  const premiumProjections = Array.isArray(latest?.premiumProjections)
    ? latest.premiumProjections
    : [];
  const premiumScenarioRows = premiumScenarioRowsFrom(premiumProjections);
  const projectionMeta = premiumProjections.find((projection) => projection?.baselineCapturedAtUtc)
    || premiumProjections[0];
  const runningSpot = asNumber(latest?.spotPrice, latestEvent?.entrySpot);
  const visiblePremiumScenarioRows = visiblePremiumScenarioRowsFrom(premiumScenarioRows, runningSpot);
  const visibleProjectionStrikeCount = visiblePremiumScenarioRows.length;

  return (
    <section className="gamma-blast-panel" aria-label="Gamma Blast monitor">
      <div className="gamma-blast-heading">
        <div className="gamma-blast-title">
          <div className="gamma-blast-icon"><ShieldCheck size={18} strokeWidth={2.3} /></div>
          <div>
            <p>GB-V1.0 DETECTOR</p>
            <h2>Gamma Blast monitor</h2>
          </div>
        </div>
        <div className="gamma-blast-heading-actions">
          <span className={`gamma-blast-connection ${loading ? "loading" : error ? "error" : "ready"}`}>
            <span />{loading ? "Loading" : error ? "Unavailable" : "Mongo-backed"}
          </span>
          <button type="button" className="gamma-blast-reload" onClick={onReload} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="gamma-blast-premium-tabs" role="tablist" aria-label="Gamma Blast premium views">
        <button
          type="button"
          role="tab"
          aria-selected={premiumView === "CURRENT"}
          className={premiumView === "CURRENT" ? "active" : ""}
          onClick={() => setPremiumView("CURRENT")}
        >
          PREMIUM LIVE
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={premiumView === "HISTORY"}
          className={premiumView === "HISTORY" ? "active" : ""}
          onClick={() => setPremiumView("HISTORY")}
        >
          PREMIUM HISTORY
        </button>
      </div>

      {error ? (
        <div className="gamma-blast-feedback error" role="alert">
          Gamma Blast history is temporarily unavailable. The live Greeks stream is unaffected.
        </div>
      ) : (
        <>
          <div className="gamma-blast-current">
            <div>
              <div className="gamma-blast-status-heading">
                <span className="gamma-blast-label">DETECTOR STATUS</span>
                <strong className={`gamma-blast-signal ${signalTone}`}>{signal}</strong>
              </div>
              <div className="gamma-blast-status-details">
                <small>{liveState.description}</small>
                {latest && <small className="gamma-blast-live-status">Live reading: {latest.status || "--"} · {eventTime(latest.capturedAtUtc)} IST</small>}
              </div>
            </div>
          <div className="gamma-blast-current-scores">
            <span><small>CALL SCORE</small><strong>{latestCallScore != null ? formatValue(latestCallScore, 1) : "--"}</strong></span>
            <span><small>PUT SCORE</small><strong>{latestPutScore != null ? formatValue(latestPutScore, 1) : "--"}</strong></span>
            <span><small>SPOT</small><strong>{latest?.spotPrice != null ? formatValue(latest.spotPrice, 2) : latestEvent ? formatValue(latestEvent.entrySpot, 2) : "--"}</strong></span>
          </div>

        </div>

          <div className="gamma-blast-stats" aria-label="Gamma Blast outcome summary">
            {[
              ["Episodes", stats?.totalEvents],
              ["Bullish", stats?.bullishEvents],
              ["Bearish", stats?.bearishEvents],
              ["Volatility", stats?.volatilityEvents],
              ["Wins", stats?.wins],
              ["Losses", stats?.losses],
            ].map(([label, value]) => (
              <div className="gamma-blast-stat" key={label}>
                <span>{label}</span>
                <strong>{value ?? "--"}</strong>
              </div>
            ))}
          </div>

          {!loading && !events.length && (
            <div className="gamma-blast-qualification" aria-label="Gamma Blast qualification progress">
              <div className="gamma-blast-qualification-copy">
                <strong>{hasLiveScores ? "Live scores are available" : "Waiting for a complete score"}</strong>
                <span>
                  {hasLiveScores
                    ? `A signal requires a score of ${GAMMA_BLAST_TRIGGER_SCORE}+ for ${GAMMA_BLAST_REQUIRED_READINGS} consecutive readings.`
                    : "The detector will score the next complete Angel One Greeks reading."}
                  {latest?.reason ? ` Latest result: ${latest.reason}.` : ""}
                </span>
              </div>
              {hasLiveScores && (
                <div className="gamma-blast-progress">
                  <div className="gamma-blast-progress-label">
                    <span>Strongest current score</span>
                    <strong>{formatValue(strongestScore, 1)} / {GAMMA_BLAST_TRIGGER_SCORE}</strong>
                  </div>
                  <div className="gamma-blast-progress-track" aria-hidden="true">
                    <span style={{ width: `${scoreProgress}%` }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {premiumView === "CURRENT" && !!visiblePremiumScenarioRows.length && (
            <section className="gamma-blast-projection" aria-label="Gamma Blast premium scenario map">
              <div className="gamma-blast-projection-heading">
                <div>
                  <span className="gamma-blast-label">PREMIUM SCENARIO MAP</span>
                  <strong>Estimated option premium top and bottom</strong>
                  <small>5 strikes below + nearest spot strike + 5 strikes above · Greek-based scenario estimate</small>
                </div>
                <span className="gamma-blast-projection-count">
                  {visibleProjectionStrikeCount} strikes shown
                </span>
              </div>
              <GammaBlastPremiumScenarioTable
                rows={visiblePremiumScenarioRows}
                runningSpot={runningSpot}
                priceRange={priceRange}
                ariaLabel="Current premium scenario map"
              />
              <div className="gamma-blast-projection-notes">
                <span>Baseline {projectionMeta?.baselineAvailable === false ? "fallback to current reading" : eventTime(projectionMeta?.baselineCapturedAtUtc) + " IST"}</span>
                <small>Top and bottom are estimates from delta, gamma, vega and theta; they are not guaranteed market highs or lows.</small>
              </div>
            </section>
          )}

          {!!events.length && (
            <div className="gamma-blast-event-wrap">
              <table className="gamma-blast-event-table">
                <thead>
                  <tr><th>TIME</th><th>SIGNAL</th><th>CALL</th><th>PUT</th><th>SPOT</th><th>RESULT</th></tr>
                </thead>
                <tbody>
                  {events.slice(0, 5).map((event) => (
                    <tr key={event.id || `${event.signal}-${event.signalTimeUtc}`}>
                      <td>{eventTime(event.signalTimeUtc)}</td>
                      <td><span className={`gamma-blast-event-signal ${eventSignalTone(event.signal)}`}>{event.signal || "--"}</span></td>
                      <td>{formatValue(event.callScore, 1)}</td>
                      <td>{formatValue(event.putScore, 1)}</td>
                      <td>{formatValue(event.entrySpot, 2)}</td>
                      <td className={`gamma-blast-result ${String(event.result || "OPEN").toLowerCase()}`}>{event.result || "OPEN"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {premiumView === "HISTORY" && (
        <GammaBlastPremiumMapHistory
          history={premiumMapHistory}
          loading={premiumMapHistoryLoading}
          error={premiumMapHistoryError}
          onReload={onReloadPremiumMapHistory}
          sessionDate={premiumMapHistoryDate}
          onSessionDateChange={onPremiumMapHistoryDateChange}
        />
      )}
    </section>
  );
}

const GRAPH_SERIES = [
  { key: "call", label: "CALL", color: "#008a0b", fill: "rgba(0, 138, 11, .08)" },
  { key: "put", label: "PUT", color: "#ef1717", fill: "rgba(239, 23, 23, .06)" },
  { key: "difference", label: "DIFFERENCE", color: "#253247", fill: "rgba(37, 50, 71, .04)" },
];

const graphMetricLabel = (metric) => (metric.key === "iv" ? "IV" : metric.label);

function GreeksGraphView({ history }) {
  const [selectedMetric, setSelectedMetric] = useState("vega");
  const [visibleSeries, setVisibleSeries] = useState({ call: true, put: true, difference: false });
  const [chartRange, setChartRange] = useState("ALL");
  const metric = METRICS.find((item) => item.key === selectedMetric) || METRICS[0];
  const metricLabel = graphMetricLabel(metric);
  const chartHistory = [...history].sort((left, right) => String(left.time).localeCompare(String(right.time)));
  const chartRangeSize = chartRange === "ALL" ? chartHistory.length : Number(chartRange);
  const visibleChartHistory = chartRangeSize >= chartHistory.length
    ? chartHistory
    : chartHistory.slice(-chartRangeSize);
  const current = metricValuesForPoint(history[history.length - 1], metric);
  const currentDifference = graphDiff(current);

  const chartData = {
    labels: visibleChartHistory.map((point) => point.time),
    datasets: GRAPH_SERIES.map((series) => ({
      label: series.key === "difference" ? series.label : `${series.label} ${metricLabel}`,
      data: visibleChartHistory.map((point) => {
        const values = metricValuesForPoint(point, metric);
        return series.key === "difference" ? graphDiff(values) : values[series.key];
      }),
      borderColor: series.color,
      backgroundColor: series.fill,
      borderWidth: 4,
      pointRadius: visibleChartHistory.length > 35 ? 0 : 2.5,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      tension: 0.28,
      fill: false,
      hidden: !visibleSeries[series.key],
      spanGaps: true,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        display: false,
      },
      title: { display: false },
      tooltip: {
        titleFont: { size: 17, weight: "700" },
        bodyFont: { size: 17, weight: "600" },
        footerFont: { size: 17, weight: "600" },
        padding: 12,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatValue(context.parsed.y, metric.precision)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(148, 163, 184, .22)" },
        ticks: { color: "#64748b", maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
      },
      y: {
        grid: {
          color: (context) => (context.tick.value === 0 ? "#111827" : "rgba(148, 163, 184, .22)"),
          lineWidth: (context) => (context.tick.value === 0 ? 4 : 1),
        },
        ticks: {
          color: "#64748b",
          callback: (value) => formatValue(value, metric.precision),
        },
      },
    },
  };

  const toggleSeries = (seriesKey) => {
    setVisibleSeries((currentVisibility) => ({
      ...currentVisibility,
      [seriesKey]: !currentVisibility[seriesKey],
    }));
  };

  return (
    <section className="greeks-graph-view" aria-label="Greeks graph view">
      <div className="greeks-graph-metric-tabs" role="tablist" aria-label="Greek metric">
        {METRICS.map((item) => (
          <button
            type="button"
            role="tab"
            aria-selected={selectedMetric === item.key}
            className={selectedMetric === item.key ? "active" : ""}
            onClick={() => setSelectedMetric(item.key)}
            key={item.key}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="greeks-graph-summary-grid">
        <article className="greeks-graph-summary-card graph-call-card">
          <strong>CALL {metricLabel}</strong>
          <span>{formatValue(current.call, metric.precision)}</span>
        </article>
        <article className="greeks-graph-summary-card graph-put-card">
          <strong>PUT {metricLabel}</strong>
          <span>{formatValue(current.put, metric.precision)}</span>
        </article>
        <article className="greeks-graph-summary-card graph-difference-card">
          <strong>DIFFERENCE</strong>
          <span className={toneFor(currentDifference)}>{signedValue(currentDifference, metric.precision)}</span>
        </article>
      </div>

      <div className="greeks-graph-layout">
        <article className="greeks-chart-panel">
          <div className="greeks-chart-toolbar">
            <div>
              <strong>{metric.label} FLOW</strong>
            </div>
            <div className="greeks-chart-controls">
              <div className="greeks-chart-range" aria-label="Chart range">
                <span>VIEW</span>
                {[{ value: "ALL", label: "ALL" }, { value: "120", label: "120" }, { value: "60", label: "60" }, { value: "30", label: "30" }].map((range) => (
                  <button
                    type="button"
                    className={chartRange === range.value ? "active" : ""}
                    aria-pressed={chartRange === range.value}
                    onClick={() => setChartRange(range.value)}
                    key={range.value}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
              <div className="greeks-series-toggle" aria-label="Chart series">
                {GRAPH_SERIES.map((series) => (
                  <button
                    type="button"
                    className={visibleSeries[series.key] ? "active" : ""}
                    aria-pressed={visibleSeries[series.key]}
                    onClick={() => toggleSeries(series.key)}
                    key={series.key}
                  >
                    <span style={{ backgroundColor: series.color }} />{series.key === "difference" ? series.label : `${series.label} ${metricLabel}`}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="greeks-chart-wrap">
            <Line data={chartData} options={chartOptions} />
          </div>
        </article>

        <article className="greeks-graph-history-panel">
          <div className="greeks-graph-history-heading">
            <div>
              <strong>{metric.label} HISTORY</strong>
            </div>
            <span>{history.length}</span>
          </div>
          <div className="greeks-graph-table-wrap">
            <table className="greeks-graph-table">
              <thead>
                <tr><th>TIME</th><th>CALL {metricLabel}</th><th>PUT {metricLabel}</th><th>DIFFERENCE</th><th>TREND</th></tr>
              </thead>
              <tbody>
                {[...history].reverse().map((point) => {
                  const values = metricValuesForPoint(point, metric);
                  const difference = graphDiff(values);
                  return (
                    <tr key={`${selectedMetric}-${point.id || point.time}`}>
                      <td>{point.time}</td>
                      <td>{formatValue(values.call, metric.precision)}</td>
                      <td>{formatValue(values.put, metric.precision)}</td>
                      <td className={toneFor(difference)}>{signedValue(difference, metric.precision)}</td>
                      <td><span className={`graph-table-trend ${trendToneFor(difference)}`}>{trendFor(difference)}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </article>
      </div>
    </section>
  );
}

export default function GreeksAnalyser() {
  const { history: angelOneHistory, isConnected, lastUpdated } = useAngelOneGreeksWebsocket(
    environment.angelOneGreeksWebsocketUrl,
    environment.angelOneGreeksTopic,
  );
  const {
    latest: gammaBlastLatest,
    events: gammaBlastEvents,
    stats: gammaBlastStats,
    loading: gammaBlastLoading,
    error: gammaBlastError,
    reload: reloadGammaBlast,
  } = useAngelOneGammaBlast(lastUpdated);
  const { snapshot: runtimeMarketData } = useAngelOneRuntimeMarketData(lastUpdated);
  const [selectedDate, setSelectedDate] = useState(() => marketDate());
  const [premiumMapHistoryDate, setPremiumMapHistoryDate] = useState(() => marketDate());
  const [expiry, setExpiry] = useState("--");
  const [activeTab, setActiveTab] = useState("GAMMA_BLAST");
  const [isRefreshing, setIsRefreshing] = useState(false);

  usePageTitle();

  const liveHistory = useMemo(
    () => angelOneHistory.map(snapshotFromAngelOne).filter(Boolean).slice(-MAX_INTRADAY_POINTS),
    [angelOneHistory],
  );

  const history = liveHistory;
  const openingValues = history[history.length - 1]?.openingMetrics || history[0]?.metrics;
  const currentValues = history[history.length - 1]?.metrics || {};
  const currentSpot = history[history.length - 1]?.spot;
  const runtimeIndexQuote = runtimeMarketData?.quotes?.find(
    (quote) => String(quote?.symbolToken || "") === "99926000",
  );
  const runtimeOpenPrice = asNumber(runtimeIndexQuote?.openPrice);
  const openingSpot = runtimeOpenPrice !== null && runtimeOpenPrice > 0
    ? runtimeOpenPrice
    : asNumber(runtimeMarketData?.openingSpotPrice);
  const currentExpiry = history[history.length - 1]?.expiry || expiry;
  const {
    history: premiumMapHistory,
    loading: premiumMapHistoryLoading,
    error: premiumMapHistoryError,
    reload: reloadPremiumMapHistory,
  } = useAngelOneGammaBlastPremiumMapHistory(undefined, premiumMapHistoryDate);
  const headerDate = formatHeaderDate(history[history.length - 1]?.date || selectedDate);
  const headerTime = formatHeaderTime(lastUpdated || history[history.length - 1]?.time);

  const summaries = useMemo(() => Object.fromEntries(METRICS.map((metric) => {
    const opening = openingValues?.[metric.key] || { call: null, put: null };
    const current = currentValues?.[metric.key] || { call: null, put: null };
    const currentDifference = diff(current);
    const openingDifference = diff(opening);
    const storedChanges = history[history.length - 1]?.changeMetrics?.[metric.key];
    const changes = storedChanges && (storedChanges.call !== null || storedChanges.put !== null)
      ? storedChanges
      : change(current, opening);
    const changeDifference = diff(changes);
    return [metric.key, {
      opening: { ...opening, diff: openingDifference },
      current: { ...current, diff: currentDifference },
      change: { ...changes, diff: changeDifference },
      netChange: changeDifference,
    }];
  })), [currentValues, history, openingValues]);

  const spotChange = currentSpot !== null && currentSpot !== undefined && openingSpot !== null && openingSpot !== undefined
    ? currentSpot - openingSpot
    : null;

  const handleRefresh = () => {
    setIsRefreshing(true);
    void reloadGammaBlast();
    void reloadPremiumMapHistory();
    window.setTimeout(() => setIsRefreshing(false), 650);
  };

  const reloadGammaBlastViews = () => {
    void reloadGammaBlast();
    void reloadPremiumMapHistory();
  };

  return (
    <main className={`greeks-analyser-page${activeTab === "GAMMA_BLAST" ? " gamma-blast-page" : ""}`}>
      <header className="greeks-stock-header">
        <nav className="greeks-stock-nav">
          <div className="greeks-stock-left-nav">
            <div className="greeks-stock-logo-container">
              <img
                src="/assets/images/noptionwave-logo-1.png"
                className="greeks-stock-header-logo"
                alt="noptionwave logo"
              />
            </div>
          </div>
          <div className="greeks-stock-right-nav">
            <div className="greeks-stock-header-item">
              <span className="greeks-stock-header-line">Date : {headerDate}</span>
              <span className="greeks-stock-header-line">Time : {headerTime}</span>
            </div>
          </div>
        </nav>
      </header>

      <div className="greeks-tab-container">
        <div className="greeks-tab" role="tablist" aria-label="Greeks analyser views">
          {ANALYSER_TABS.map((tab) => (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`greeks-tablinks ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
              key={tab.key}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`greeks-analyser-shell${activeTab === "GAMMA_BLAST" ? " gamma-blast-shell" : ""}`}>
        <section className="greeks-toolbar" aria-label="Greeks filters">
          <label className="greeks-control">
            <span>INDEX</span>
            <span className="greeks-select-wrap"><select defaultValue="NIFTY" aria-label="Index"><option>NIFTY</option></select><ChevronDown size={15} /></span>
          </label>
          <label className="greeks-control greeks-date-control">
            <span>SESSION DATE</span>
            <span className="greeks-date-wrap"><CalendarDays size={15} /><input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} aria-label="Session date" /></span>
          </label>
          <label className="greeks-control">
            <span>EXPIRY</span>
            <span className="greeks-select-wrap"><select value={currentExpiry} onChange={(event) => setExpiry(event.target.value)} aria-label="Expiry" disabled={!history.length}><option>{currentExpiry}</option></select><ChevronDown size={15} /></span>
          </label>
          <div className="greeks-toolbar-market" aria-label="NIFTY market summary">
            <div className="greeks-toolbar-market-main">
              <span className="market-label"><span className="market-dot" />NIFTY</span>
              <strong>{formatValue(currentSpot, 2)}</strong>
              <span className={`spot-change ${toneFor(spotChange)}`}>
                <strong>{signedValue(spotChange, 2)}</strong>
              </span>
            </div>
            <div className="greeks-toolbar-market-meta">
              <span className="market-meta-item"><span>Open</span><strong>{formatValue(openingSpot, 2)}</strong></span>
              <span className="market-meta-item"><span>Expiry</span><strong>{currentExpiry}</strong></span>
              <span className="market-meta-item"><span>Last update</span><strong>{lastUpdated ? new Date(lastUpdated).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false }) : "--"}</strong></span>
            </div>
          </div>
          <div className="greeks-toolbar-note">
            <div className={`stream-status ${isConnected ? "connected" : "disconnected"}`}>
              {isConnected ? <Wifi size={15} /> : <WifiOff size={15} />}
              <span>{isConnected ? "Stream connected" : "Waiting for today’s 09:15 data"}</span>
            </div>
            <button type="button" className="greeks-refresh-button" onClick={handleRefresh} aria-label="Refresh Greeks data">
              <RefreshCw size={16} className={isRefreshing ? "refresh-spin" : ""} />
              <span>Refresh</span>
            </button>
            <span className="greeks-update-note"><Clock3 size={15} /><span>Updates every minute</span></span>
          </div>
        </section>

        {activeTab === "GAMMA_BLAST" ? (
          <GammaBlastMonitor
            latest={gammaBlastLatest}
            events={gammaBlastEvents}
            stats={gammaBlastStats}
            loading={gammaBlastLoading}
            error={gammaBlastError}
            onReload={reloadGammaBlastViews}
            premiumMapHistory={premiumMapHistory}
            premiumMapHistoryLoading={premiumMapHistoryLoading}
            premiumMapHistoryError={premiumMapHistoryError}
            onReloadPremiumMapHistory={reloadPremiumMapHistory}
            premiumMapHistoryDate={premiumMapHistoryDate}
            onPremiumMapHistoryDateChange={setPremiumMapHistoryDate}
          />
        ) : !history.length ? (
          <section className="greeks-empty-state" aria-live="polite">
            <Clock3 size={28} />
            <strong>No Greeks data available for today</strong>
            <span>Today’s day-open data will appear here after the 09:15 market-data load.</span>
          </section>
        ) : activeTab === "GREEKS_ANALYSER" ? (
          <>
        <section className="greeks-summary-grid">
          {METRICS.map((metric) => <MetricSummaryCard key={metric.key} metric={metric} summary={summaries[metric.key]} />)}
        </section>

        <section className="greeks-history-grid">
          {METRICS.map((metric) => <HistoryCard key={metric.key} metric={metric} history={history} />)}
        </section>

          </>
        ) : (
          <GreeksGraphView history={history} />
        )}
      </div>
      <footer className="greeks-page-footer">noptionwave © 2026</footer>
    </main>
  );
}
